// functions/src/vocab 응답 형식(packages/admin-model AdminVocab)

export type VocabProgress = 'not_started' | 'generating' | 'in_review' | 'approved_unpublished' | 'published';

export interface VocabStats {
  generating: number;
  inReview: number;
  rework: number;
  approvedUnpublished: number;
  published: number;
  defaultPublished: number;
  searchPublished: number;
}

/** 어휘 용도(D-V8). 없으면 기본 카드 후보 */
export type VocabPurpose = 'default_card' | 'search_only';

export const PURPOSE_LABEL: Record<VocabPurpose, { label: string; color: string }> = {
  default_card: { label: '기본 카드 후보', color: 'default' },
  search_only: { label: '검색 이미지 전용', color: 'cyan' },
};

export const purposeOf = (v: { purpose?: VocabPurpose | null }): VocabPurpose => v.purpose ?? 'default_card';

export interface VocabDraft {
  text: string;
  sourceWord: string | null;
  category: string;
  vocabType: 'core' | 'fringe';
  keywords: string[];
  sources: { sourceId: string }[];
  rationale: string;
  imageType: 'realistic' | 'character';
  priority: number;
  existingCardId: string | null;
  slug: string | null;
  /** 생성 피사체 설명(한글·영문). 없으면 어휘 이름으로 생성 */
  imageSubject?: string | null;
  purpose?: VocabPurpose | null;
  /** 주제별 후보 초안의 주제(D-V10) */
  theme?: string | null;
}

export interface VocabItem extends VocabDraft {
  vocabId: string;
  stats: VocabStats;
  progress: VocabProgress;
  createdAt: string;
  updatedAt: string;
}

export interface ImportPreview {
  candidateCount: number;
  toCreate: VocabDraft[];
  existing: { key: string; text: string; vocabId: string; differences: string[]; fill: DefaultFill | null }[];
  /** 동음이의어 후보인데 같은 표기의 저장 어휘에 슬러그가 없어 보류된 후보 */
  needsSlug?: { key: string; text: string; vocabIds: string[] }[];
  mergedAsKeywords: { word: string; into: string }[];
  errors: { line: number; message: string }[];
}

/** 저장된 어휘에 채울 CSV 기본값(빈 슬러그, 빠진 키워드) */
export interface DefaultFill {
  slug?: string;
  keywords?: string[];
  /** 기본 카드 선정(D-V11)에 따른 용도·우선순위 */
  purpose?: VocabPurpose;
  priority?: number;
}

export interface ApplyDefaultsResult {
  updated: { vocabId: string; changed: string[] }[];
  skipped: { vocabId: string; reason: string }[];
}

/** 후보 키: 슬러그, 없으면 표기(packages/admin-model candidateKey와 같다) */
export const candidateKey = (v: { text: string; slug: string | null }): string => v.slug ?? v.text;

/** 표기 · 슬러그 (키워드). 뜻 확인 힌트를 한 줄로 */
export function vocabLabel(v: { text: string; slug?: string | null; keywords?: string[] }, maxKeywords = 3): string {
  const kw = (v.keywords ?? []).slice(0, maxKeywords);
  return `${v.text}${v.slug ? ` · ${v.slug}` : ''}${kw.length ? ` (${kw.join(', ')})` : ''}`;
}

export interface ImportResult {
  created: number;
  ids: string[];
  skippedExisting: number;
  remaining: number;
}

/** 앱 고정 카테고리(D-V4) */
export const CATEGORY_LABEL: Record<string, string> = {
  people: '사람',
  place: '장소',
  foodObject: '음식·사물',
  activity: '활동',
  emotionState: '감정·상태',
  sentenceSupport: '문장보조',
};

export const PROGRESS_LABEL: Record<VocabProgress, { label: string; color: string }> = {
  not_started: { label: '이미지 미생성', color: 'default' },
  generating: { label: '생성 중', color: 'processing' },
  in_review: { label: '검수 대기', color: 'warning' },
  approved_unpublished: { label: '승인 후 미게시', color: 'purple' },
  published: { label: '게시 완료', color: 'success' },
};

export const FIELD_LABEL: Record<string, string> = {
  category: '카테고리',
  vocabType: '핵심/주변',
  existingCardId: '기존 카드',
  keywords: '키워드',
  sourceWord: '원문 표기',
  slug: '슬러그',
};

export function matchesQuery(v: VocabItem, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [v.text, v.vocabId, v.sourceWord ?? '', v.existingCardId ?? '', v.slug ?? '', ...v.keywords].some((x) => x.toLowerCase().includes(s));
}

// ---- 통합 어휘 목록(GET /vocab/unified) ----

export type UnifiedStatus = 'managed' | 'candidate' | 'app_only';

export interface AppCardRef {
  cardId: string;
  text: string;
  category: string;
  imageResourceId: string;
  /** 앱 번들에만 있는 이미지는 null */
  imageUrl: string | null;
}

export interface AppSearchRef {
  docId: string;
  displayName: string;
  tags: string[];
  imageUrl: string | null;
  path: string | null;
  fileId: string | null;
  /** 카테고리 폴더가 아닌 경로(on_fct_lab, private 등) */
  other: boolean;
}

export interface UnifiedRow {
  key: string;
  status: UnifiedStatus;
  text: string;
  slug: string | null;
  category: string | null;
  keywords: string[];
  vocabId: string | null;
  vocabType: 'core' | 'fringe' | null;
  /** 용도(관리 중·후보). 앱에만 있음은 null */
  purpose?: VocabPurpose | null;
  /** 주제별 후보 초안의 주제(D-V10) */
  theme?: string | null;
  progress: VocabProgress | null;
  stats: VocabStats | null;
  candidateKey: string | null;
  card: AppCardRef | null;
  searchImages: AppSearchRef[];
  related: AppSearchRef[];
  needsConfirm: { cards: AppCardRef[]; searchImages: AppSearchRef[] };
  /** 사람이 확정한 연결 키(card:… / search:…) */
  manualLinks?: string[];
  /** 사람이 연결을 해제한 이미지 */
  excluded?: { cards: AppCardRef[]; searchImages: AppSearchRef[] };
}

export interface UnifiedList {
  rows: UnifiedRow[];
  /** 이미 어드민 자산으로 가져온 앱 이미지 출처(card:… / search:… / r2:…) */
  imported?: string[];
  /** 가져온 출처 → 자산 ID(참고해 새로 만들기) */
  importedAssets?: Record<string, string>;
  sources: { cards: boolean; searchImages: boolean; releaseLabel: string | null };
  warnings: string[];
}

export const STATUS_LABEL: Record<UnifiedStatus, { label: string; color: string }> = {
  managed: { label: '관리 중', color: 'blue' },
  candidate: { label: '후보', color: 'default' },
  app_only: { label: '앱에만 있음', color: 'orange' },
};

/** 표기·슬러그·키워드·카드 ID·검색 이미지 이름·태그에서 찾는다 */
export function matchesUnified(r: UnifiedRow, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [r.text, r.slug ?? '', r.vocabId ?? '', r.card?.cardId ?? '', ...r.keywords, ...r.searchImages.flatMap((i) => [i.displayName, i.fileId ?? '', ...i.tags])].some((x) => x.toLowerCase().includes(s));
}

/** 카테고리 폴더가 아닌 경로(on_fct_lab, private) 이미지만 있는 줄 */
export const onlyOtherPaths = (r: UnifiedRow) => !r.card && r.searchImages.length > 0 && r.searchImages.every((i) => i.other);

/** 주제 필터 값: 주제 이름, 또는 주제 없는 어휘(연구 근거 후보·직접 추가·앱 항목) */
export const NO_THEME = '__none__';
export const themeMatches = (v: { theme?: string | null }, theme: string | null) => !theme || (theme === NO_THEME ? !v.theme : v.theme === theme);
export const themeOptions = (rows: readonly { theme?: string | null }[]) => [
  ...[...new Set(rows.map((r) => r.theme).filter((t): t is string => !!t))].sort().map((t) => ({ value: t, label: t })),
  { value: NO_THEME, label: '주제 없음(연구 근거·직접 추가)' },
];
